import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Placeholder, discountOf } from '../components/ProductCard.jsx';
import ProductCard from '../components/ProductCard.jsx';
import { HeartIcon } from '../components/Icons.jsx';
import { useStore, stockFor } from '../context/StoreContext.jsx';
import { money, sizeRank } from '../config/brand.js';
import { ik, ikSet } from '../utils/imagekit.js';
import NotFound from './NotFound.jsx';

export default function Product() {
  const { id } = useParams();
  const { products, addToCart, wishlist, toggleWishlist } = useStore();
  const product = products.find((p) => String(p.id) === String(id));
  const [img, setImg] = useState(0);
  const [size, setSize] = useState('');
  const [qty, setQty] = useState(1);

  useEffect(() => { setImg(0); setSize(''); setQty(1); }, [id]);

  const related = useMemo(
    () => (product ? products.filter((p) => p.mainCategory === product.mainCategory && p.id !== product.id).slice(0, 4) : []),
    [products, product]
  );

  if (!product) return <NotFound message="This product is no longer available." />;

  const sizes = [...(product.sizes || [])].sort((a, b) => sizeRank(a.size) - sizeRank(b.size));
  const hasSizes = sizes.length > 0;
  const available = stockFor(product, hasSizes ? size || null : null);
  const soldOut = (product.stock || 0) <= 0;
  const off = discountOf(product);
  const gallery = product.images && product.images.length ? product.images : [];
  const saved = wishlist.includes(product.id);
  const lowStock = hasSizes && size && available > 0 && available <= 3;

  return (
    <div className="page">
      <nav className="crumbs" aria-label="Breadcrumb">
        <Link to="/">Home</Link> / <Link to={`/shop/${product.mainCategory}`}>{product.mainCategory === 'clothing' ? 'Clothing' : 'Handbags'}</Link> / <span>{product.name}</span>
      </nav>

      <div className="pdp">
        <div className="pdp__gallery">
          <div className="pdp__main">
            {gallery[img] ? <img src={ik(gallery[img], 1000)} srcSet={ikSet(gallery[img], [500, 800, 1000, 1400])} sizes="(max-width: 900px) 100vw, 50vw" alt={product.name} /> : <Placeholder name={product.name} />}
            {off > 0 && !soldOut && <span className="badge badge--sale">-{off}%</span>}
          </div>
          {gallery.length > 1 && (
            <div className="pdp__thumbs">
              {gallery.map((g, i) => (
                <button key={g + i} type="button" className={i === img ? 'is-active' : ''} onClick={() => setImg(i)} aria-label={`Show image ${i + 1}`}>
                  <img src={ik(g, 160)} alt="" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="pdp__info">
          <p className="eyebrow eyebrow--left">{product.category}</p>
          <h1 className="pdp__title">{product.name}</h1>
          <p className="pdp__price">
            <span>{money(product.price)}</span>
            {off > 0 && <s>{money(product.originalPrice)}</s>}
          </p>
          {product.sku && <p className="muted">SKU {product.sku}</p>}

          {hasSizes && (
            <div className="pdp__block">
              <p className="pdp__label">Size {size && <strong>{size}</strong>}</p>
              <div className="sizes" role="group" aria-label="Select size">
                {sizes.map((s) => (
                  <button
                    key={s.size}
                    type="button"
                    className={`sizebtn sizebtn--lg${size === s.size ? ' is-active' : ''}${s.stock <= 0 ? ' is-out' : ''}`}
                    disabled={s.stock <= 0}
                    onClick={() => { setSize(s.size); setQty(1); }}
                    aria-label={s.stock <= 0 ? `${s.size}, sold out` : s.size}
                  >{s.size}</button>
                ))}
              </div>
              {lowStock && <p className="hint">Only {available} left in this size.</p>}
            </div>
          )}

          <div className="pdp__block pdp__buy">
            <div className="qty">
              <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease quantity">-</button>
              <span>{qty}</span>
              <button type="button" onClick={() => setQty((q) => Math.min(q + 1, Math.max(1, available || 1), 10))} aria-label="Increase quantity">+</button>
            </div>
            <button type="button" className="btn btn--grow" disabled={soldOut} onClick={() => addToCart(product, size, qty)}>
              {soldOut ? 'Sold out' : 'Add to bag'}
            </button>
            <button type="button" className={`iconbtn iconbtn--box${saved ? ' is-on' : ''}`} onClick={() => toggleWishlist(product.id)} aria-label={saved ? 'Remove from wishlist' : 'Add to wishlist'}>
              <HeartIcon filled={saved} />
            </button>
          </div>

          {product.description && <div className="pdp__section"><h3>Description</h3><p>{product.description}</p></div>}
          {(product.color || product.material) && (
            <div className="pdp__section">
              <h3>Details</h3>
              <dl>
                {product.color && (<><dt>Colour</dt><dd>{product.color}</dd></>)}
                {product.material && (<><dt>Material</dt><dd>{product.material}</dd></>)}
              </dl>
            </div>
          )}
          {product.fitNotes && <div className="pdp__section"><h3>Fit and size notes</h3><p>{product.fitNotes}</p></div>}
          <div className="pdp__section"><h3>Delivery</h3><p>Cash on delivery across Pakistan. We confirm every order by phone or email before dispatch.</p></div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="section section--rule">
          <h2 className="section__title">You may also like</h2>
          <div className="grid">{related.map((p) => <ProductCard key={p.id} product={p} />)}</div>
        </section>
      )}
    </div>
  );
}
